// src/pages/payment/PaymentPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';
import { useCart } from '../../hooks/useCart';
import { useAuth } from '../../hooks/useAuth';

const PaymentPage = () => {
  const { cart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  // ✅ Shipping data from Checkout page
  const shippingData = JSON.parse(
    sessionStorage.getItem('shippingData') || '{}'
  );

  // ✅ Safety redirect
  useEffect(() => {
    if (!cart.length || !shippingData.fullName) {
      navigate('/checkout');
    }
  }, [cart, shippingData, navigate]);

  // ✅ Totals
  const subtotal = cart.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const shippingCost = subtotal > 50 ? 0 : 8.99;
  const total = Number((subtotal + shippingCost).toFixed(2));

  // ✅ Cloud Function
  const createCheckoutSession = httpsCallable(
    functions,
    'createCheckoutSession'
  );

  const handlePayment = async () => {
    setProcessing(true);
    setError(null);

    try {
      const result = await createCheckoutSession({
        cartItems: cart.map(item => ({
          id: item.id,
          title: item.title,
          imageUrl: item.imageUrl || '',
          price: Number(item.price),
          quantity: Number(item.quantity),
        })),
        shippingData: {
          fullName: shippingData.fullName,
          email: shippingData.email,
          phone: shippingData.phone || '',
          address: shippingData.address,
          city: shippingData.city,
          province: shippingData.province,
          postalCode: shippingData.postalCode,
        },
        shipping: shippingCost,
        origin: window.location.origin,
      });

      if (!result.data?.url) {
        throw new Error('Stripe session URL missing');
      }

      // 🔁 Redirect to Stripe
      window.location.href = result.data.url;
    } catch (err) {
      console.error('Payment init error:', err);
      setError('Unable to start payment. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <div className="payment-page">
      <div className="container">
        <h1 className="payment-title">Secure Payment</h1>
        <p className="checkout-step">Step 2 of 2: Payment</p>

        <div className="checkout-layout">
          {/* LEFT */}
          <div className="checkout-form-section">
            <div className="form-card">
              <div className="payment-security">
                <p>🔒 Secured by Stripe</p>
                <p>Credit/Debit Cards • Apple Pay • Google Pay</p>
              </div>

              {error && (
                <div className="payment-error">{error}</div>
              )}

              <button
                onClick={handlePayment}
                disabled={processing}
                className="payment-submit-btn"
              >
                {processing
                  ? 'Redirecting to Stripe...'
                  : `Pay $${total.toFixed(2)} CAD`}
              </button>

              <Link to="/checkout" className="back-to-shipping">
                ← Back to Shipping
              </Link>
            </div>
          </div>

          {/* RIGHT */}
          <div className="checkout-summary-section">
            <div className="summary-card">
              <h2>Order Summary</h2>

              <div className="summary-items">
                {cart.map(item => (
                  <div key={item.id} className="summary-item">
                    <span>
                      {item.title} × {item.quantity}
                    </span>
                    <span>
                      ${(item.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="summary-line">
                <span>Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>

              <div className="summary-line">
                <span>Shipping</span>
                <span>
                  {shippingCost === 0
                    ? 'Free'
                    : `$${shippingCost.toFixed(2)}`}
                </span>
              </div>

              <div className="summary-total">
                <strong>Total</strong>
                <strong>${total.toFixed(2)} CAD</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
